// The rule builder — a sentence, not a form dump.
//
// Site engineers fill this in, not developers, so it reads as English and every device dropdown
// lists only what actually exists at the selected site. Nothing is pre-selected: per spec §2.1
// a rule is configured by hand, per device, and the builder must never guess which register an
// engineer meant.
//
// Validation runs live and blocks saving (spec §9). A half-configured rule that only fails when
// it tries to start a pump during an incident is far worse than one that refuses to save.

import React, { useMemo, useState, useEffect } from "react";
import { Plus, Trash2, AlertTriangle, Info, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  SAFETY_CLASSES, OPERATORS, RESET_MODES, ON_FAIL, ALERT_SEVERITIES,
  CHANNEL_LABELS, ANALOG_SIGNAL_TYPES,
  emptyRule, emptyCondition, sourceKey, availableChannels,
  isAnalogOperator, validateRule, errorsFor,
} from "../../lib/automationModel";

const NONE = "_none";

function Label({ children, hint }) {
  return (
    <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
      {children}
      {hint && <span className="normal-case tracking-normal font-normal text-gray-400"> · {hint}</span>}
    </span>
  );
}

function FieldErrors({ errors, field }) {
  const msgs = errorsFor(errors, field);
  if (!msgs.length) return null;
  return (
    <div className="flex items-start gap-1 text-[11px] text-red-600 mt-1">
      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={2.5} />
      <span>{msgs.join(" ")}</span>
    </div>
  );
}

function Keyword({ children }) {
  return (
    <span className="text-[10px] font-mono uppercase tracking-widest text-gray-400 w-20 shrink-0 text-right pt-2 select-none">
      {children}
    </span>
  );
}

// ── Source picker ─────────────────────────────────────────────────────────────

function SourcePicker({ value, options, optionByKey, onChange, placeholder, disabled }) {
  const assets = options.filter((o) => o.group === "asset");
  const registers = options.filter((o) => o.group === "io");
  const current = value ? sourceKey(value) : "";

  return (
    <Select
      value={current || NONE}
      disabled={disabled}
      onValueChange={(k) => onChange(k === NONE ? null : optionByKey[k]?.source || null)}
    >
      <SelectTrigger className="bg-gray-50 border-gray-200 h-8 text-xs min-w-[180px] max-w-[260px]">
        <SelectValue placeholder={placeholder || "Pick a device…"} />
      </SelectTrigger>
      <SelectContent className="bg-white border-gray-200 text-gray-900 max-h-[320px]">
        <SelectItem value={NONE}>{placeholder || "Pick a device…"}</SelectItem>
        {assets.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-[10px] uppercase tracking-widest text-gray-400">Registered devices</SelectLabel>
            {assets.map((o) => (
              <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
            ))}
          </SelectGroup>
        )}
        {registers.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-[10px] uppercase tracking-widest text-gray-400">
              Raw registers · commissioning
            </SelectLabel>
            {registers.map((o) => (
              <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
            ))}
          </SelectGroup>
        )}
      </SelectContent>
    </Select>
  );
}

function ChannelPicker({ source, asset, onChange }) {
  const channels = availableChannels(asset);
  if (!asset || channels.length < 2) return null;
  return (
    <Select
      value={source.channel || "primary"}
      onValueChange={(c) => onChange({ ...source, channel: c })}
    >
      <SelectTrigger className="bg-gray-50 border-gray-200 h-8 text-xs w-[150px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-white border-gray-200 text-gray-900">
        {channels.map((c) => (
          <SelectItem key={c} value={c}>{CHANNEL_LABELS[c] || c}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── One condition row ─────────────────────────────────────────────────────────

function ConditionRow({
  condition, keyword, ctx, errors, field, onChange, onRemove, allowDelay, liveValues,
}) {
  const { sourceOptions, optionByKey, assetsById } = ctx;
  const src = condition.source;
  const asset = src?.kind === "asset" ? assetsById[src.asset_id] : null;
  const opt = src ? optionByKey[sourceKey(src)] : null;
  const analog = isAnalogOperator(condition.op);

  // Digital points get ON/OFF rather than a bare 1/0 — the operator is reading a relay, not a
  // number. Derived from the point's own signal type; never from its name.
  const isDigital = opt && !ANALOG_SIGNAL_TYPES.includes(opt.signal_type) && !analog;

  const live = asset && liveValues?.[asset.asset_id];

  return (
    <div className="flex flex-wrap items-start gap-1.5 py-1">
      <Keyword>{keyword}</Keyword>

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <SourcePicker
            value={src}
            options={sourceOptions}
            optionByKey={optionByKey}
            onChange={(s) => onChange({ ...condition, source: s, unit: optionByKey[sourceKey(s || {})]?.unit || "" })}
          />
          {asset && (
            <ChannelPicker source={src} asset={asset} onChange={(s) => onChange({ ...condition, source: s })} />
          )}

          <Select value={condition.op} onValueChange={(op) => onChange({ ...condition, op })}>
            <SelectTrigger className="bg-white border-gray-200 h-8 text-xs w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-gray-200 text-gray-900">
              {OPERATORS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>

          {isDigital ? (
            <Select value={String(condition.value || "")} onValueChange={(v) => onChange({ ...condition, value: v })}>
              <SelectTrigger className="bg-orange-50 border-orange-200 h-8 text-xs w-[90px]"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900">
                <SelectItem value="1">ON</SelectItem>
                <SelectItem value="0">OFF</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Input
              type="number"
              step="any"
              value={condition.value}
              onChange={(e) => onChange({ ...condition, value: e.target.value })}
              placeholder="value"
              className="bg-orange-50 border-orange-200 h-8 text-xs w-[92px] tabular-nums"
            />
          )}

          {!isDigital && (
            <Input
              value={condition.unit || ""}
              onChange={(e) => onChange({ ...condition, unit: e.target.value })}
              placeholder="unit"
              className="bg-gray-50 border-gray-200 h-8 text-xs w-[68px]"
            />
          )}

          {allowDelay && (
            <>
              <span className="text-xs text-gray-400">for</span>
              <Input
                type="number"
                min="0"
                value={condition.for_seconds}
                onChange={(e) => onChange({ ...condition, for_seconds: e.target.value })}
                placeholder={analog ? "required" : "optional"}
                className={`h-8 text-xs w-[92px] tabular-nums ${
                  analog && !condition.for_seconds ? "bg-red-50 border-red-200" : "bg-orange-50 border-orange-200"
                }`}
              />
              <span className="text-xs text-gray-400">s</span>
            </>
          )}

          <button
            type="button"
            onClick={onRemove}
            className="text-gray-300 hover:text-red-600 transition-colors p-1"
            aria-label="Remove condition"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>

        {live?.value != null && (
          <div className="text-[11px] text-emerald-700 font-medium pl-1">
            reads {live.value}{live.unit ? ` ${live.unit}` : ""} right now
          </div>
        )}
        <FieldErrors errors={errors} field={field} />
      </div>
    </div>
  );
}

// ── Builder ───────────────────────────────────────────────────────────────────

export default function RuleBuilder({
  open, onOpenChange, siteId, initialRule, ctx, liveValues, onSave, saving, saveError,
}) {
  const [rule, setRule] = useState(() => initialRule || emptyRule(siteId));
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setRule(initialRule || emptyRule(siteId));
      setTouched(false);
    }
  }, [open, initialRule, siteId]);

  const errors = useMemo(() => validateRule(rule, ctx), [rule, ctx]);
  const showErrors = touched;

  const set = (patch) => setRule((r) => ({ ...r, ...patch }));
  const setCondition = (list, i, next) => {
    const copy = [...list];
    copy[i] = next;
    return copy;
  };

  const writes = (rule.actions || []).filter((a) => a.type === "write");
  const needsFeedback = rule.safety_class !== "supervisory" && writes.length > 0;

  const outputOptions = useMemo(
    () => (ctx.sourceOptions || []).filter((o) => o.writable),
    [ctx.sourceOptions]
  );

  const submit = () => {
    setTouched(true);
    if (errors.length) return;
    onSave(rule);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border border-gray-200 text-gray-900 max-w-4xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{rule.rule_id ? "Edit rule" : "New cause & effect rule"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 text-sm">

          {/* Identity */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1 md:col-span-2">
              <Label>Rule name *</Label>
              <Input
                value={rule.name}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="e.g. Transfer pump auto-start on UG tank low"
                className="bg-gray-50 border-gray-200"
              />
              {showErrors && <FieldErrors errors={errors} field="name" />}
            </label>
            <label className="flex flex-col gap-1">
              <Label>Priority</Label>
              <Input
                type="number"
                value={rule.priority}
                onChange={(e) => set({ priority: Number(e.target.value) })}
                className="bg-gray-50 border-gray-200 tabular-nums"
              />
              <span className="text-[10px] text-gray-400">Lower wins when two rules drive one output.</span>
            </label>
          </div>

          {/* Safety class */}
          <div className="border-t border-gray-200 pt-4">
            <Label>Safety class</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
              {SAFETY_CLASSES.map((s) => {
                const active = rule.safety_class === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => set({ safety_class: s.value })}
                    className={`text-left rounded-lg border p-3 transition-colors ${
                      active ? "border-orange-400 bg-orange-50 ring-1 ring-orange-200" : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="font-bold text-xs">{s.label}</div>
                    <div className="text-[11px] text-gray-500 mt-1 leading-snug">{s.blurb}</div>
                  </button>
                );
              })}
            </div>
            {rule.safety_class !== "supervisory" && (
              <div className="flex items-start gap-2 mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={2.5} />
                <span>
                  This rule will physically actuate equipment. It still saves as <b>Disabled</b> — arming it is a
                  separate, deliberate step, and life-safety interlocks must remain hardwired at the panel.
                </span>
              </div>
            )}
          </div>

          {/* Trigger */}
          <div className="border-t border-gray-200 pt-4">
            <Label hint="all conditions must hold">Trigger</Label>
            <div className="mt-1">
              {(rule.trigger?.all || []).map((c, i) => (
                <ConditionRow
                  key={i}
                  condition={c}
                  keyword={i === 0 ? "when" : "and"}
                  ctx={ctx}
                  liveValues={liveValues}
                  errors={showErrors ? errors : []}
                  field={`trigger.${i}`}
                  allowDelay
                  onChange={(next) => set({ trigger: { all: setCondition(rule.trigger.all, i, next) } })}
                  onRemove={() => set({ trigger: { all: rule.trigger.all.filter((_, j) => j !== i) } })}
                />
              ))}
              {showErrors && <FieldErrors errors={errors} field="trigger" />}
              <Button
                type="button" variant="ghost" size="sm"
                className="text-xs text-orange-600 hover:text-orange-700 mt-1"
                onClick={() => set({ trigger: { all: [...(rule.trigger?.all || []), emptyCondition()] } })}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add condition
              </Button>
            </div>
          </div>

          {/* Permissives */}
          <div className="border-t border-gray-200 pt-4">
            <Label hint="checked at the moment of firing">Permissives</Label>
            <div className="flex items-start gap-1.5 text-[11px] text-gray-500 mt-1 mb-1">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>A permissive with no fresh telemetry blocks the action. An unreadable &ldquo;pump healthy&rdquo; signal is never assumed healthy.</span>
            </div>
            {(rule.permissives || []).map((c, i) => (
              <ConditionRow
                key={i}
                condition={c}
                keyword={i === 0 ? "only if" : "and"}
                ctx={ctx}
                liveValues={liveValues}
                errors={showErrors ? errors : []}
                field={`permissives.${i}`}
                allowDelay={false}
                onChange={(next) => set({ permissives: setCondition(rule.permissives, i, next) })}
                onRemove={() => set({ permissives: rule.permissives.filter((_, j) => j !== i) })}
              />
            ))}
            <Button
              type="button" variant="ghost" size="sm"
              className="text-xs text-orange-600 hover:text-orange-700 mt-1"
              onClick={() => set({ permissives: [...(rule.permissives || []), emptyCondition()] })}
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add permissive
            </Button>
          </div>

          {/* Actions */}
          <div className="border-t border-gray-200 pt-4">
            <Label>Actions</Label>
            <div className="mt-1 space-y-1">
              {(rule.actions || []).map((a, i) => (
                <div key={i} className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-1.5 py-1">
                    <Keyword>{i === 0 ? "then" : "and"}</Keyword>
                    <Select
                      value={a.type}
                      onValueChange={(t) => {
                        const copy = [...rule.actions];
                        copy[i] = t === "write"
                          ? { type: "write", target: null, value: "1" }
                          : { type: "alert", severity: "warning", title: "", message: "" };
                        set({ actions: copy });
                      }}
                    >
                      <SelectTrigger className="bg-white border-gray-200 h-8 text-xs w-[120px]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 text-gray-900">
                        <SelectItem value="write">actuate</SelectItem>
                        <SelectItem value="alert">raise alert</SelectItem>
                      </SelectContent>
                    </Select>

                    {a.type === "write" ? (
                      <>
                        <Select
                          value={String(a.value ?? "1")}
                          onValueChange={(v) => {
                            const copy = [...rule.actions];
                            copy[i] = { ...a, value: v };
                            set({ actions: copy });
                          }}
                        >
                          <SelectTrigger className="bg-white border-gray-200 h-8 text-xs w-[92px]"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-gray-200 text-gray-900">
                            <SelectItem value="1">Start</SelectItem>
                            <SelectItem value="0">Stop</SelectItem>
                          </SelectContent>
                        </Select>
                        <SourcePicker
                          value={a.target}
                          options={outputOptions}
                          optionByKey={ctx.optionByKey}
                          placeholder="Pick an output…"
                          onChange={(s) => {
                            const copy = [...rule.actions];
                            copy[i] = { ...a, target: s };
                            set({ actions: copy });
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <Select
                          value={a.severity || "warning"}
                          onValueChange={(v) => {
                            const copy = [...rule.actions];
                            copy[i] = { ...a, severity: v };
                            set({ actions: copy });
                          }}
                        >
                          <SelectTrigger className="bg-white border-gray-200 h-8 text-xs w-[110px]"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-gray-200 text-gray-900">
                            {ALERT_SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input
                          value={a.title || ""}
                          onChange={(e) => {
                            const copy = [...rule.actions];
                            copy[i] = { ...a, title: e.target.value };
                            set({ actions: copy });
                          }}
                          placeholder="Alert title"
                          className="bg-gray-50 border-gray-200 h-8 text-xs w-[260px]"
                        />
                      </>
                    )}

                    <button
                      type="button"
                      onClick={() => set({ actions: rule.actions.filter((_, j) => j !== i) })}
                      className="text-gray-300 hover:text-red-600 transition-colors p-1"
                      aria-label="Remove action"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  </div>
                  {showErrors && <div className="pl-[86px]"><FieldErrors errors={errors} field={`actions.${i}`} /></div>}
                </div>
              ))}
              {showErrors && <FieldErrors errors={errors} field="actions" />}
              <div className="flex gap-1">
                <Button
                  type="button" variant="ghost" size="sm"
                  className="text-xs text-orange-600 hover:text-orange-700"
                  onClick={() => set({ actions: [...(rule.actions || []), { type: "alert", severity: "warning", title: "", message: "" }] })}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add alert
                </Button>
                {rule.safety_class !== "supervisory" && (
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="text-xs text-red-600 hover:text-red-700"
                    onClick={() => set({ actions: [...(rule.actions || []), { type: "write", target: null, value: "1" }] })}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add actuation
                  </Button>
                )}
              </div>
              {rule.safety_class === "supervisory" && (
                <p className="text-[11px] text-gray-500">
                  A supervisory rule only alerts. Switch to a control class above to add an actuation.
                </p>
              )}
            </div>
          </div>

          {/* Feedback */}
          {needsFeedback && (
            <div className="border-t border-gray-200 pt-4">
              <Label hint="required for any rule that writes">Confirmation</Label>
              <div className="flex flex-wrap items-center gap-1.5 py-1 mt-1">
                <Keyword>confirm</Keyword>
                <span className="text-xs text-gray-400">via</span>
                <SourcePicker
                  value={rule.feedback?.source}
                  options={ctx.sourceOptions}
                  optionByKey={ctx.optionByKey}
                  placeholder="Pick a feedback point…"
                  onChange={(s) => set({ feedback: { ...(rule.feedback || { expect: 1, timeout_s: 20, on_fail: "critical_alert" }), source: s } })}
                />
                <span className="text-xs text-gray-400">reads</span>
                <Select
                  value={String(rule.feedback?.expect ?? 1)}
                  onValueChange={(v) => set({ feedback: { ...(rule.feedback || {}), expect: Number(v) } })}
                >
                  <SelectTrigger className="bg-white border-gray-200 h-8 text-xs w-[86px]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">
                    <SelectItem value="1">ON</SelectItem>
                    <SelectItem value="0">OFF</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-gray-400">within</span>
                <Input
                  type="number" min="1"
                  value={rule.feedback?.timeout_s ?? 20}
                  onChange={(e) => set({ feedback: { ...(rule.feedback || {}), timeout_s: e.target.value } })}
                  className="bg-orange-50 border-orange-200 h-8 text-xs w-[80px] tabular-nums"
                />
                <span className="text-xs text-gray-400">s, else</span>
                <Select
                  value={rule.feedback?.on_fail || "critical_alert"}
                  onValueChange={(v) => set({ feedback: { ...(rule.feedback || {}), on_fail: v } })}
                >
                  <SelectTrigger className="bg-white border-gray-200 h-8 text-xs w-[190px]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">
                    {ON_FAIL.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {showErrors && <FieldErrors errors={errors} field="feedback" />}
              <p className="text-[11px] text-gray-500 mt-1">
                Without this, a command that silently failed looks identical to one that worked.
              </p>
            </div>
          )}

          {/* Reset + limits */}
          <div className="border-t border-gray-200 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Reset</Label>
              <Select value={rule.reset?.mode || "auto_on_clear"} onValueChange={(v) => set({ reset: { ...rule.reset, mode: v } })}>
                <SelectTrigger className="bg-gray-50 border-gray-200 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900">
                  {RESET_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {rule.reset?.mode === "auto_on_clear" && (
                <label className="flex flex-col gap-1">
                  <Label hint="how far past the setpoint before releasing">Hysteresis band *</Label>
                  <Input
                    type="number" step="any"
                    value={rule.reset?.hysteresis ?? ""}
                    onChange={(e) => set({ reset: { ...rule.reset, hysteresis: e.target.value } })}
                    placeholder="e.g. 10"
                    className="bg-gray-50 border-gray-200 h-8 text-xs tabular-nums"
                  />
                  <span className="text-[10px] text-gray-400">Start at 50, stop at 60 — never both at 50.</span>
                  {showErrors && <FieldErrors errors={errors} field="reset" />}
                </label>
              )}
              <label className="flex flex-col gap-1">
                <Label hint="anti-hunt">Minimum off time</Label>
                <Input
                  type="number" min="0"
                  value={rule.reset?.min_off_seconds ?? ""}
                  onChange={(e) => set({ reset: { ...rule.reset, min_off_seconds: e.target.value } })}
                  placeholder="seconds"
                  className="bg-gray-50 border-gray-200 h-8 text-xs tabular-nums"
                />
                <span className="text-[10px] text-gray-400">A flapping sensor short-cycling a motor destroys it.</span>
              </label>
            </div>

            <div className="space-y-2">
              <Label>Safety limits</Label>
              <label className="flex flex-col gap-1">
                <Label hint="circuit breaker">Max actuations per hour</Label>
                <Input
                  type="number" min="1"
                  value={rule.limits?.max_actuations_per_hour ?? 6}
                  onChange={(e) => set({ limits: { ...rule.limits, max_actuations_per_hour: e.target.value } })}
                  className="bg-gray-50 border-gray-200 h-8 text-xs tabular-nums"
                />
                <span className="text-[10px] text-gray-400">Exceeding this stops the rule and raises a critical alert.</span>
              </label>
              <label className="flex flex-col gap-1">
                <Label>Cooldown between actuations</Label>
                <Input
                  type="number" min="0"
                  value={rule.limits?.cooldown_seconds ?? 60}
                  onChange={(e) => set({ limits: { ...rule.limits, cooldown_seconds: e.target.value } })}
                  placeholder="seconds"
                  className="bg-gray-50 border-gray-200 h-8 text-xs tabular-nums"
                />
              </label>
              <label className="flex flex-col gap-1">
                <Label>Notes</Label>
                <Input
                  value={rule.description || ""}
                  onChange={(e) => set({ description: e.target.value })}
                  placeholder="Why this rule exists"
                  className="bg-gray-50 border-gray-200 h-8 text-xs"
                />
              </label>
            </div>
          </div>

          {showErrors && errors.length > 0 && (
            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5} />
              <span>{errors.length} thing{errors.length === 1 ? "" : "s"} still to fix before this rule can be saved.</span>
            </div>
          )}

          {saveError && (
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5} />
              <span>{saveError}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white">
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            {rule.rule_id ? "Save changes" : "Create rule (disabled)"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
